import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import locationIcon from "../assets/location.svg"
import timeIcon     from "../assets/time.svg"
import moneyIcon    from "../assets/money.svg"
import remoteIcon   from "../assets/remote.svg"
import levelIcon    from "../assets/level.svg"
import dateIcon     from "../assets/date.svg"
import appIcon      from "../assets/anyroundai-icon.png"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const BRAND_LIGHT = "#D5FE57"
const JOB_API = "https://jobscanner-e1dd4.ue.r.appspot.com/network/job"
const BRANDFETCH_CLIENT_ID = "1idn4glWklGs5yLvUdW"
const MAX_W = "64rem"

function daysAgoLabel(job) {
  let ts = null
  if (job?.posted_at_ts) ts = job.posted_at_ts * 1000
  else if (job?.posted_at) ts = new Date(job.posted_at).getTime()
  if (!ts || isNaN(ts)) return ""
  const diffMs = Date.now() - ts
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffDays < 0) return "Just now"
  if (diffDays === 0) {
    const hrs = Math.floor(diffMs / 3600000)
    if (hrs < 1) { const mins = Math.floor(diffMs / 60000); return mins < 1 ? "Just now" : `${mins}m ago` }
    return `${hrs}h ago`
  }
  if (diffDays === 1) return "1 day ago"
  if (diffDays < 30) return `${diffDays} days ago`
  if (diffDays < 60) return "1 month ago"
  return `${Math.floor(diffDays / 30)} months ago`
}

function initials(name = "") {
  return name.split(/[\s_\-.]+/).filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase() || "??"
}

function salaryLabel(job) {
  const min = job?.salary_min, max = job?.salary_max
  if (min && max) {
    const isINR = min > 10000 || max > 10000
    const fmt = n => isINR ? `₹${Number(n).toLocaleString("en-IN")}` : `$${Math.round(n / 1000)}K/yr`
    return `${fmt(min)} – ${fmt(max)}`
  }
  return null
}

function CompanyLogo({ companyName, size = 52 }) {
  const [err, setErr] = useState(false)
  const domain = companyName
    ? companyName.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "") + ".com"
    : null
  const bfUrl = domain ? `https://cdn.brandfetch.io/${domain}?c=${BRANDFETCH_CLIENT_ID}` : null

  if (!err && bfUrl) {
    return (
      <img
        src={bfUrl}
        alt={companyName}
        onError={() => setErr(true)}
        loading="lazy"
        style={{
          width: size, height: size, borderRadius: 14,
          objectFit: "contain", background: "#181512",
          border: "1px solid rgba(255,255,255,0.07)", flexShrink: 0,
        }}
      />
    )
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: 14,
      background: "#1a2e25", border: "1px solid rgba(255,255,255,0.07)",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <span style={{ fontSize: size * 0.3, fontWeight: 700, color: BRAND_LIGHT, fontFamily: FF }}>
        {initials(companyName)}
      </span>
    </div>
  )
}

function FactPill({ icon, label }) {
  if (!label) return null
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      padding: "7px 13px", borderRadius: 9,
      background: "#161412", border: "1px solid #252220",
    }}>
      <img src={icon} alt="" width={13} height={13} style={{ flexShrink: 0, opacity: 0.4 }} />
      <span style={{ fontSize: 12.5, fontWeight: 500, color: "#8a8480", fontFamily: FF, whiteSpace: "nowrap" }}>
        {label}
      </span>
    </div>
  )
}

function Sk({ w = "100%", h = 14 }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: 6,
      background: "#1e1c19", animation: "shimmer 1.4s ease-in-out infinite",
    }} />
  )
}

export default function JobPublicPage() {
  const { jobId } = useParams()
  const [job, setJob]           = useState(null)
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [applyHov, setApplyHov] = useState(false)

  useEffect(() => {
    setLoading(true)
    fetch(`${JOB_API}/${jobId}`)
      .then(r => { if (!r.ok) throw new Error("not found"); return r.json() })
      .then(data => {
        if (!data.success || !data.listing) throw new Error("not found")
        setJob(data.listing)
      })
      .catch(() => setError("Job not found or no longer available."))
      .finally(() => setLoading(false))
  }, [jobId])

  const company     = job?.company || "—"
  const title       = job?.role    || "—"
  const ago         = job ? daysAgoLabel(job) : ""
  const sal         = job ? salaryLabel(job) : null
  const level       = job?.seniority_key && job.seniority_key !== "UNKNOWN"
    ? job.seniority_key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : null
  const skills      = Array.isArray(job?.skills) ? job.skills : []
  const description = job?.description || ""
  const isHtml      = /<[a-z][\s\S]*>/i.test(description)

  const facts = [
    { icon: locationIcon, label: job?.canonical_location_id || job?.location },
    { icon: remoteIcon,   label: job?.work_type },
    { icon: moneyIcon,    label: sal },
    { icon: levelIcon,    label: level },
    { icon: dateIcon,     label: job?.experience_min != null ? `${job.experience_min}+ yrs exp` : null },
    { icon: timeIcon,     label: "Full-time" },
  ].filter(f => f.label)

  return (
    <div style={{ minHeight: "100vh", background: "#0d0c0b", color: "#f0ede9", fontFamily: FF }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes shimmer { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #2a2520; border-radius: 2px; }
        .jdesc-body h1,.jdesc-body h2,.jdesc-body h3,.jdesc-body h4 {
          font-size: 14px; font-weight: 700; color: #c9c5c0;
          margin: 20px 0 8px; font-family: ${FF}; letter-spacing: -0.01em; line-height: 1.3;
        }
        .jdesc-body p  { margin: 0 0 12px; }
        .jdesc-body ul,.jdesc-body ol { padding-left: 20px; margin: 0 0 14px; }
        .jdesc-body li { margin-bottom: 6px; }
        .jdesc-body strong,.jdesc-body b { color: #c9c5c0; font-weight: 600; }
        .jdesc-body a  { color: #D5FE57; text-decoration: none; }
        .jdesc-body a:hover { text-decoration: underline; }
      `}</style>

      {/* ── Topbar ── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 50,
        background: "#0f0e0d",
        borderBottom: "1px solid #1e1c19",
      }}>
        <div style={{
          maxWidth: MAX_W, margin: "0 auto",
          padding: "0 24px", height: 52,
          display: "flex", alignItems: "center",
        }}>
          <Link to="/dashboard/home" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
            <img src={appIcon} alt="Resolut" style={{ width: 26, height: 26, borderRadius: 7, objectFit: "contain" }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: "#f0ede9", fontFamily: FF, letterSpacing: "-0.025em" }}>
              Resolut
            </span>
          </Link>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: MAX_W, margin: "0 auto", padding: "40px 24px 80px" }}>

        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "fadeUp 0.2s ease" }}>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: "#1e1c19", animation: "shimmer 1.4s ease-in-out infinite", flexShrink: 0 }} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                <Sk w="35%" h={13} />
                <Sk w="60%" h={22} />
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {[90, 100, 80, 110, 95].map((w, i) => <Sk key={i} w={w} h={34} />)}
            </div>
            <div style={{ height: 1, background: "#1e1c19", margin: "8px 0" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[...Array(14)].map((_, i) => <Sk key={i} w={i % 5 === 4 ? "50%" : "100%"} h={14} />)}
            </div>
          </div>
        )}

        {error && !loading && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <p style={{ fontSize: 16, fontWeight: 700, color: "#f0ede9", fontFamily: FF, marginBottom: 8 }}>
              Job not found
            </p>
            <p style={{ fontSize: 13, color: "#6b6560", fontFamily: FF, marginBottom: 24 }}>
              {error}
            </p>
            <Link
              to="/dashboard/home"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                height: 38, padding: "0 18px",
                background: BRAND_LIGHT, border: "none", borderRadius: 9,
                fontSize: 13, fontWeight: 700, fontFamily: FF, color: "#0e1a14",
                textDecoration: "none",
              }}
            >
              Browse jobs
            </Link>
          </div>
        )}

        {!loading && !error && job && (
          <div style={{ animation: "fadeUp 0.25s cubic-bezier(0.22,1,0.36,1)" }}>

            {/* ── Job header ── */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, minWidth: 0, flex: 1 }}>
                <CompanyLogo companyName={company} size={52} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: "0 0 5px", fontSize: 13, fontWeight: 600, color: "#5a5550", fontFamily: FF }}>
                    {company}
                    {ago && <span style={{ fontWeight: 400, color: "#3d3830" }}> · {ago}</span>}
                  </p>
                  <h1 style={{
                    margin: 0, fontSize: 26, fontWeight: 800, color: "#f0ede9",
                    fontFamily: FF, letterSpacing: "-0.03em", lineHeight: 1.2,
                  }}>
                    {title}
                  </h1>
                </div>
              </div>

              <button
                onClick={() => job?.job_url && window.open(job.job_url, "_blank")}
                onMouseEnter={() => setApplyHov(true)}
                onMouseLeave={() => setApplyHov(false)}
                style={{
                  height: 42, padding: "0 22px", borderRadius: 10, flexShrink: 0,
                  display: "inline-flex", alignItems: "center", gap: 7,
                  background: BRAND_LIGHT, color: "#0a0a0a",
                  fontSize: 13, fontWeight: 700, fontFamily: FF,
                  border: "none", cursor: "pointer",
                  boxShadow: applyHov ? "0 4px 20px rgba(213,254,87,0.35)" : "0 2px 10px rgba(213,254,87,0.18)",
                  transform: applyHov ? "translateY(-1px)" : "none",
                  transition: "transform 0.12s, box-shadow 0.12s",
                  whiteSpace: "nowrap",
                }}
              >
                Apply now
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="#0a0a0a" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transform: applyHov ? "translateX(2px)" : "none", transition: "transform 0.15s" }}>
                  <path d="M3 8h10M9 4l4 4-4 4" />
                </svg>
              </button>
            </div>

            {/* ── Fact pills ── */}
            {facts.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 28 }}>
                {facts.map((f, i) => <FactPill key={i} icon={f.icon} label={f.label} />)}
              </div>
            )}

            {/* ── Skills ── */}
            {skills.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <p style={{ margin: "0 0 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#3d3830", fontFamily: FF }}>
                  Required Skills
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                  {skills.map((s, i) => (
                    <span key={i} style={{
                      display: "inline-flex", alignItems: "center",
                      padding: "5px 13px", borderRadius: 7,
                      fontSize: 12.5, fontWeight: 500, fontFamily: FF,
                      color: "#c4b5fd",
                      background: "rgba(139,92,246,0.07)",
                      border: "1px solid rgba(139,92,246,0.15)",
                      whiteSpace: "nowrap",
                    }}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ height: 1, background: "#1a1815", marginBottom: 28 }} />

            {/* ── Description ── */}
            <div>
              <p style={{ margin: "0 0 16px", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#3d3830", fontFamily: FF }}>
                About this role
              </p>
              {isHtml ? (
                <div
                  className="jdesc-body"
                  style={{ fontSize: 14, color: "#7a7672", lineHeight: 1.9, fontFamily: FF, wordBreak: "break-word" }}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              ) : (
                <div style={{ fontSize: 14, color: "#7a7672", lineHeight: 1.9, fontFamily: FF, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {description || "No description available."}
                </div>
              )}
            </div>

            {/* ── Bottom CTA ── */}
            <div style={{
              marginTop: 48, paddingTop: 28,
              borderTop: "1px solid #1a1815",
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
              flexWrap: "wrap",
            }}>
              <div>
                <p style={{ margin: "0 0 3px", fontSize: 15, fontWeight: 700, color: "#f0ede9", fontFamily: FF, letterSpacing: "-0.02em" }}>
                  Interested in this role?
                </p>
                <p style={{ margin: 0, fontSize: 13, color: "#5a5550", fontFamily: FF }}>
                  Apply directly on {company}'s website
                </p>
              </div>
              <button
                onClick={() => job?.job_url && window.open(job.job_url, "_blank")}
                style={{
                  height: 40, padding: "0 22px", borderRadius: 9,
                  display: "inline-flex", alignItems: "center", gap: 7,
                  background: BRAND_LIGHT, color: "#0a0a0a",
                  fontSize: 13, fontWeight: 700, fontFamily: FF,
                  border: "none", cursor: "pointer",
                }}
              >
                Apply now →
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  )
}
